import React, { useState, useEffect, Suspense, lazy } from "react";
import { 
  Sparkles,
  ChevronRight
} from "lucide-react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import { faro } from '@grafana/faro-web-sdk';
import Header from "./features/portal/components/Header";
import AboutSection from "./features/landing/components/AboutSection";
import AdvisoryPillarsSection from "./features/landing/components/AdvisoryPillarsSection";
import SelfSelectionCTA from "./features/landing/components/SelfSelectionCTA";
import StructuralComparisonTable from "./features/landing/components/StructuralComparisonTable";
import QASection from "./features/landing/components/QASection";
import OnboardingSection from "./features/landing/components/OnboardingSection";
import HeroSection from "./features/landing/components/HeroSection";
import BeyondTheStack from "./features/landing/components/BeyondTheStack";
import OutcomeStoriesSection from "./features/landing/components/OutcomeStoriesSection";
import DynamicCta from "./features/cta/DynamicCta";
import KybInvoiceModal from "./features/intake/components/KybInvoiceModal";
import Footer from "./features/portal/components/Footer";

// Safe Lazy Loading Helper to handle stale dynamic chunk hashes after production deployments
const safeLazy = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const isChunkError =
        error instanceof Error &&
        (error.message.includes("Failed to fetch dynamically imported module") ||
         error.message.includes("Loading chunk"));
      if (isChunkError && typeof window !== "undefined") {
        const reloadKey = "chunk_retry_" + window.location.pathname;
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, "true");
          window.location.reload();
        }
      }
      throw error;
    }
  });

// Lazy Loaded Components
const FiduciaryBriefPage = safeLazy(() => import("./features/advisory/components/FiduciaryBriefPage"));
const MasterEngagementAgreementPage = safeLazy(() => import("./features/advisory/components/MasterEngagementAgreementPage"));
const BoardAdvisoryAgreementPage = safeLazy(() => import("./features/advisory/components/BoardAdvisoryAgreementPage"));
const AdminDashboard = safeLazy(() => import("./features/admin/dashboard/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const AdvisoryPage = safeLazy(() => import("./features/advisory/components/AdvisoryPage"));
const PortfolioPage = safeLazy(() => import("./features/portfolio/components/PortfolioPage"));
const IntelligencePage = safeLazy(() => import("./features/intelligence/components/IntelligencePage"));
const CredentialsPage = safeLazy(() => import("./features/credential/components/CredentialsPage"));
const EarlyAdoptionLanding = safeLazy(() => import("./pages/landing/EarlyAdoptionLanding"));
const IndustrialRDLanding = safeLazy(() => import("./pages/landing/IndustrialRDLanding"));
const ConstructionAIPage = safeLazy(() => import("./pages/landing/ConstructionAIPage"));
const LegalAIPage = safeLazy(() => import("./pages/landing/LegalAIPage"));
const RecruiterLanding = safeLazy(() => import("./pages/landing/RecruiterLanding"));
const IntakeForm = safeLazy(() => import("./features/intake/components/IntakeForm"));
const AdvisoryIntakeForm = safeLazy(() => import("./features/intake/components/AdvisoryIntakeForm"));

// Authentication
import { AuthProvider } from "./features/auth/AuthContext";
import AuthModal from "./features/auth/AuthModal";
import ErrorBoundary from "./features/portal/components/ErrorBoundary";
import { Helmet } from "react-helmet-async";

const PageLoadingFallback = () => <div className="min-h-screen flex items-center justify-center bg-brand-cream"><div className="w-8 h-8 border-4 border-brand-burgundy border-t-transparent rounded-full animate-spin"></div></div>;

// Component to handle scrolling to hash on navigation
function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

export default function App() {
  const [ctaHeadline, setCtaHeadline] = useState("Ready to build deterministic value?");
  const [ctaSubheading, setCtaSubheading] = useState("If your leadership team is ready to move past unmonitored testing cycles and establish an auditable, high-ROI enterprise AI capability, align with my practice via the terminal below.");
  const [ctaId, setCtaId] = useState("");
  const [loadingCta, setLoadingCta] = useState(true);

  useEffect(() => {
    let principalId = localStorage.getItem("principal_id");
    if (!principalId) {
      principalId = crypto.randomUUID();
      localStorage.setItem("principal_id", principalId);
    }
    
    fetch("/api/cta/next", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ principal_id: principalId })
    })
    .then(res => res.json())
    .then(data => {
      if (data.is_active) {
        setCtaHeadline(data.headline);
        setCtaSubheading(data.subheading);
        setCtaId(data.cta_id);
      } else {
        setCtaHeadline("");
      }
      setLoadingCta(false);
    })
    .catch(err => {
      console.error("Error fetching CTA:", err);
      setLoadingCta(false);
    });
  }, []);

  const location = useLocation();
  const isPlainPage = ["/admin", "/industrial", "/executive-search", "/construction", "/legal", "/legal-ai"].includes(location.pathname);
  const showIntakeSection = !isPlainPage && !location.pathname.startsWith("/l/") && !location.pathname.startsWith("/Downloads/");

  useEffect(() => {
    if (faro?.api) {
      faro.api.setView({ name: location.pathname });
    }
  }, [location.pathname]);

  return (
    <AuthProvider>
      <ScrollToHash />
      <div className="min-h-screen bg-brand-cream text-brand-charcoal flex flex-col font-sans selection:bg-brand-orange selection:text-white">
        
        {/* Editorial Header */}
        {!isPlainPage && <Header />}

        {/* Main Content Sections */}
        <main className="flex-grow">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={
                <>
                  <Helmet>
                  <title>Fractional CAIO | Crispin Courtenay</title>
                </Helmet>
                <HeroSection />
                <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 -mt-8 mb-16">
                  <DynamicCta placementLocation="caio_home_hero" className="shadow-lg" />
                </div>
                <div className="h-1 bg-gradient-to-r from-brand-burgundy via-brand-orange to-brand-green max-w-6xl mx-auto" />
                <AboutSection />
                <AdvisoryPillarsSection />
                <OutcomeStoriesSection />
                <SelfSelectionCTA />
                <StructuralComparisonTable />
                <QASection />
                <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 py-8">
                  <DynamicCta placementLocation="caio_home_post_qa" className="shadow-lg" />
                </div>
                <OnboardingSection />
              </>
            } />
            
            <Route path="/caio-diagnostic" element={
              <>
                <Helmet>
                  <title>CAIO Diagnostic | Crispin Courtenay</title>
                </Helmet>
                <HeroSection />
                {/* Automatically jump down to intake form */}
                <Navigate to="/#intake-form" replace />
              </>
            } />

            <Route path="/strategic-advisory" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Strategic Advisory | Crispin Courtenay</title>
                </Helmet>
                <AdvisoryPage onNavigateToIntake={() => { window.location.hash = "#intake-form"; }} />
              </Suspense>
            } />
            
            {/* Alias for backward compatibility */}
            <Route path="/advisory" element={<Navigate to="/strategic-advisory" replace />} />

            <Route path="/portfolio" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Project Portfolio | Crispin Courtenay</title>
                </Helmet>
                <PortfolioPage />
              </Suspense>
            } />

            <Route path="/insights" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Intelligence Briefs | Crispin Courtenay</title>
                </Helmet>
                <IntelligencePage />
              </Suspense>
            } />
            
            {/* Alias for backward compatibility */}
            <Route path="/blog" element={<Navigate to="/insights" replace />} />
            <Route path="/intelligence" element={<Navigate to="/insights" replace />} />

            <Route path="/credentials" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Credentials Vault | Crispin Courtenay</title>
                </Helmet>
                <CredentialsPage />
              </Suspense>
            } />

            <Route path="/executive-search" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Executive Search | Crispin Courtenay</title>
                </Helmet>
                <RecruiterLanding />
              </Suspense>
            } />
            
            <Route path="/recruiter" element={<Navigate to="/executive-search" replace />} />

            <Route path="/construction" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Construction AI Systems | Crispin Courtenay</title>
                </Helmet>
                <ConstructionAIPage />
              </Suspense>
            } />
            
            <Route path="/industrial" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Industrial R&D AI Systems | Crispin Courtenay</title>
                </Helmet>
                <IndustrialRDLanding />
              </Suspense>
            } />

            <Route path="/legal" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <Helmet>
                  <title>Legal AI Systems | Crispin Courtenay</title>
                </Helmet>
                <LegalAIPage />
              </Suspense>
            } />
            <Route path="/legal-ai" element={<Navigate to="/legal" replace />} />

            <Route path="/admin" element={
              <ErrorBoundary>
                <Helmet>
                  <title>Platform Admin | Crispin Courtenay</title>
                </Helmet>
                <Suspense fallback={<PageLoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              </ErrorBoundary>
            } />

            {/* Documents */}
            <Route path="/Downloads/Documents/FiduciaryBrief" element={
              <Suspense fallback={<PageLoadingFallback />}><FiduciaryBriefPage /></Suspense>
            } />
            <Route path="/Downloads/Documents/MasterEngagementAgreement" element={
              <Suspense fallback={<PageLoadingFallback />}><MasterEngagementAgreementPage /></Suspense>
            } />
            <Route path="/Downloads/Documents/BoardAdvisoryAgreement" element={
              <Suspense fallback={<PageLoadingFallback />}><BoardAdvisoryAgreementPage /></Suspense>
            } />

            {/* Dynamic Landing Pages */}
            <Route path="/l/:slug" element={
              <Suspense fallback={<PageLoadingFallback />}>
                <EarlyAdoptionLanding pageId={"dynamic"} />
              </Suspense>
            } />
          </Routes>
          </ErrorBoundary>
          
          {showIntakeSection && <BeyondTheStack />}
          
          {showIntakeSection && (
            <section id="intake-form" className="bg-brand-charcoal border-t-4 border-brand-burgundy py-24 md:py-28 relative">
              <div className="absolute top-0 left-1/2 w-80 h-80 bg-brand-orange/[0.02] rounded-full blur-3xl pointer-events-none -translate-x-1/2" />
              
              <div className="max-w-4xl mx-auto px-6 text-center">
                <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-espresso border border-brand-burgundy rounded-sm mb-6 font-mono text-[10px] text-brand-orange uppercase tracking-wider font-bold">
                  <Sparkles className="w-3.5 h-3.5" /> Secure Handshake Protocol
                </div>
                
                {loadingCta ? (
                  <div className="space-y-4 animate-pulse max-w-2xl mx-auto py-4">
                    <div className="h-10 bg-brand-espresso/60 border border-brand-burgundy/20 rounded-sm w-3/4 mx-auto" />
                    <div className="h-6 bg-brand-espresso/40 border border-brand-burgundy/10 rounded-sm w-5/6 mx-auto" />
                  </div>
                ) : ctaHeadline ? (
                  <div className="transition-all duration-500 ease-out">
                    <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight font-display">
                      {ctaHeadline}
                    </h2>
                    <p className="mt-4 text-brand-sand/70 text-sm md:text-base font-light max-w-2xl mx-auto leading-relaxed">
                      {ctaSubheading}
                    </p>
                  </div>
                ) : null}

                {/* Diagnostic Intake Terminal Container */}
                <div className="mt-12 text-left">
                  <Suspense fallback={<div className="h-64 animate-pulse bg-brand-espresso/40 border border-brand-burgundy/20 rounded-sm flex items-center justify-center text-xs font-mono text-brand-sand/50">INITIALIZING SECURE TERMINAL...</div>}>
                    {location.pathname === "/strategic-advisory" ? (
                      <AdvisoryIntakeForm ctaId={ctaId} />
                    ) : (
                      <IntakeForm ctaId={ctaId} />
                    )}
                  </Suspense>
                </div>

                {/* Direct manual connection channels */}
                <div className="mt-16 pt-12 border-t border-brand-espresso">
                  <span className="text-xs font-mono text-brand-sand/50 uppercase tracking-widest block mb-4 font-bold">OR DIRECTLY REACH MY NETWORK</span>
                  <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
                    <a 
                      href="mailto:crispin@aistrategy.tech" 
                      className="text-white hover:text-brand-orange font-mono text-sm transition-colors border-2 border-brand-espresso hover:border-brand-burgundy bg-brand-espresso px-6 py-3.5 rounded-sm flex items-center gap-2 shadow-lg"
                    >
                      crispin@aistrategy.tech <ChevronRight className="w-4 h-4 text-brand-orange" />
                    </a>
                    
                    <div className="text-brand-sand/50 text-xs font-mono">
                      SME Placement Agencies: <a href="https://tegus.com/" target="_blank" rel="noopener noreferrer" className="text-white font-bold hover:text-brand-orange hover:underline transition-colors">Tegus</a> / <a href="https://www.alphasights.com/" target="_blank" rel="noopener noreferrer" className="text-white font-bold hover:text-brand-orange hover:underline transition-colors">AlphaSights</a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer variant={["/", "/strategic-advisory", "/executive-search"].includes(location.pathname) ? "full" : "short"} />

        {/* Secure Authentication Modal */}
        <AuthModal />
        {/* KYB & SLA Corporate Qualification Modal */}
        <KybInvoiceModal />
      </div>
    </AuthProvider>
  );
}
